import GenericTable from "components/table";
import { EvaluationHistoryRecord, EvaluationStatus } from "types";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { HistoryRecordDetailDrawer } from "./HistoryRecordDetailDrawer";
import { useAgentEvaluation } from "layouts/agent/agent-info/agent-evaluate/context/AgentEvaluationContext";
import { useTranslation } from "hooks/useTranslation";

const StatusBadge = ({ status }: { status: EvaluationStatus }) => {
  const statusStyles: Record<EvaluationStatus, string> = {
    [EvaluationStatus.DONE]: "bg-green-100 text-green-800",
    [EvaluationStatus.PROCESSING]: "bg-blue-100 text-blue-800",
    [EvaluationStatus.ERROR]: "bg-red-100 text-red-800",
  };

  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[status]}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
};

const HistoryRecordDatatable = () => {
  const { evaluationHistoryRecords } = useAgentEvaluation();
  const { t } = useTranslation();
  const columns: ColumnDef<EvaluationHistoryRecord, any>[] = [
    {
      accessorKey: "evaluation_dataset_name",
      header: () => <p>{t("agents.evaluateSection.datasetName")}</p>,
      cell: (info: { getValue: () => string }) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    },

    {
      accessorKey: "status",
      header: () => <p>{t("agents.evaluateSection.status")}</p>,
      cell: (info: { getValue: () => EvaluationStatus }) => {
        const status = info.getValue();
        return status ? <StatusBadge status={status} /> : <span>-</span>;
      },
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("agents.evaluateSection.createdAt")}</p>,
      cell: (info: { getValue: () => string }) => {
        const value = info.getValue();
        let dateString = "-";
        if (value) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            dateString = date.toLocaleString();
          }
        }
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {dateString}
          </p>
        );
      },
    },
  ];

  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <>
      <GenericTable<EvaluationHistoryRecord>
        customQueryName="rq"
        data={evaluationHistoryRecords}
        columns={columns}
        renderRowClassName={(row) => {
          const status = row.original.status;
          return status !== EvaluationStatus.DONE ? "pointer-events-none" : "";
        }}
        onRowClick={(item) => {
          setSelectedId(item.id);
        }}
        searchPlaceholder={t("agents.evaluateSection.searchHistory")}
      />
      <HistoryRecordDetailDrawer
        id={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};

export default HistoryRecordDatatable;
